import { cn } from "../../lib/utils";

/**
 * A figure lifted out of a source paper, framed as a card inside a GPT answer
 * (Figma DS 5.0 "Figure Card", node 6066:1998).
 *
 * The design also carries the figure's journal chip, its reference block and
 * the paper's abstract line. The answer transcript only carries the image and
 * its caption today, so those parts are absent rather than faked — the card
 * shows what it actually knows.
 */
export function FigureCard({
  src,
  caption,
  label,
  className,
}: {
  src: string;
  // The figure's own caption (the markdown image's alt text), or null when the
  // answer emitted the image without one.
  caption: string | null;
  // Localized "Figure" — the accent label on the right of the header row.
  label: string;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "flex w-full flex-col gap-4 rounded-2xl bg-ds-white px-4 py-5 shadow-card",
        className
      )}
    >
      <span className="flex items-center justify-end">
        <span className="font-dm-sans text-footnote font-semibold text-ds-blueberry-500">
          {label}
        </span>
      </span>
      <img
        src={src}
        alt={caption === null ? label : caption}
        className="w-full rounded-lg border border-ds-slate-200 object-cover"
      />
      {caption !== null && (
        <span className="font-dm-sans text-footnote text-ds-slate-700">
          <span className="font-semibold text-ds-slate-950">{label}: </span>
          {caption}
        </span>
      )}
    </span>
  );
}
